// ============================================================
// Full path:
//   D:\Thyroid Consultation Software\ThyroConsult Frontend\thyroconsult-frontend\src\components\ErrorBoundary.js
//
// Safety net for unexpected errors (2 Oct 2026). Without it, any error
// while drawing a page left the visitor looking at a blank white screen.
// Now they see a short message with "Reload page" and "Go to home page".
//
// It also handles a website update while someone has the site open:
// parts of the site are downloaded only when needed, so after a new
// release an open tab can ask for a part that no longer exists. That is
// fixed by reloading once, automatically — the visitor just sees the page
// refresh.
// ============================================================
import React from 'react';
import { tNow } from '../i18n'; // the page's language (13 Oct 2026, Hindi version)

const RELOAD_FLAG = 'tc_chunk_reload_at';

const isChunkError = (error) => {
  const msg = String((error && (error.message || error.name)) || '');
  return /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module|Unexpected token '<'/i.test(msg);
};

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ThyroConsult page error:', error, info && info.componentStack);
    if (isChunkError(error)) {
      // Reload once (not again within 30 seconds, so it can never loop)
      let last = 0;
      try { last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0); } catch (_) { /* private mode */ }
      if (Date.now() - last > 30000) {
        try { sessionStorage.setItem(RELOAD_FLAG, String(Date.now())); } catch (_) { /* private mode */ }
        window.location.reload();
      }
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const updating = isChunkError(this.state.error);
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--bg, #f8fafc)' }}>
        <div style={{ maxWidth: 420, textAlign: 'center', background: '#fff', borderRadius: 14, padding: '32px 28px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }} aria-hidden="true">{updating ? '🔄' : '⚠️'}</div>
          <h1 style={{ fontSize: '1.3rem', margin: '0 0 10px' }}>
            {updating ? tNow('The website has just been updated') : tNow('Something went wrong')}
          </h1>
          <p style={{ fontSize: 14, color: '#475569', margin: '0 0 22px', lineHeight: 1.5 }}>
            {updating
              ? tNow('Please reload the page to continue.')
              : tNow('Sorry — this page could not be shown. Your information is safe. Please reload the page; if it happens again, go back to the home page and try once more.')}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>{tNow('Reload page')}</button>
            <button type="button" className="btn btn-secondary" onClick={() => { window.location.href = '/'; }}>{tNow('Go to home page')}</button>
          </div>
        </div>
      </div>
    );
  }
}
